<template>
  <BaseModal :show="show" @close="$emit('close')">
    <template #title>
      Wybierz cechę produktu
    </template>
    
    <div class="space-y-4">
      <!-- Search Input -->
      <div class="relative">
        <div class="absolute left-3 top-1/2 -translate-y-1/2">
          <font-awesome-icon icon="search" class="text-slate-400" />
        </div>
        <input 
          ref="inputEl" 
          v-model.trim="search" 
          type="text" 
          class="w-full h-10 pl-10 pr-4 border border-blue-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors" 
          placeholder="Szukaj cech produktu lub wartości" 
        />
      </div>

      <!-- Step Selection -->
      <div class="flex space-x-2 mb-4">
        <button
          type="button"
          class="flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors"
          :class="step === 'search' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
          @click="step = 'search'"
        >
          Szukaj wszystkich
        </button>
        <button
          type="button"
          class="flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors"
          :class="step === 'browse' ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
          @click="step = 'browse'"
        >
          Przeglądaj po cechach
        </button>
      </div>

      <!-- Search Results -->
      <div v-if="step === 'search'" class="max-h-80 overflow-y-auto">
        <div v-if="loading" class="text-center py-6">
          <div class="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-slate-600 mt-2 text-sm">Wyszukiwanie...</p>
        </div>
        <div v-else-if="search.trim().length < 2" class="text-center py-6 text-slate-500">
          <p class="text-sm">Zacznij pisać, aby wyszukać cechy lub wartości</p>
        </div>
        <div v-else-if="searchResults.length > 0" class="space-y-2">
          <div 
            v-for="result in searchResults" 
            :key="result.id"
            class="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <div>
              <p class="font-medium text-slate-800 text-sm mb-0">{{ result.label }}</p>
              <p class="text-xs text-slate-500 mt-0.5">{{ result.feature_name }}</p>
            </div>
            <button 
              type="button"
              class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors" 
              @click="selectFeatureValue(result)"
            >
              Wybierz
            </button>
          </div>
        </div>
        <div v-else class="text-center py-6 text-slate-500">
          <p class="text-sm">Nie znaleziono cech ani wartości</p>
        </div>
      </div>

      <!-- Browse by Features -->
      <div v-if="step === 'browse'" class="max-h-80 overflow-y-auto">
        <div v-if="loadingFeatures" class="text-center py-6">
          <div class="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-slate-600 mt-2 text-sm">Ładowanie cech...</p>
        </div>
        <div v-else-if="!selectedFeature" class="space-y-2">
          <h4 class="text-sm font-semibold text-slate-700 mb-3">Wybierz cechę:</h4>
          <div 
            v-for="feature in allFeatures" 
            :key="feature.feature_id"
            class="p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer"
            @click="selectFeature(feature)"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium text-slate-800 text-sm">{{ feature.feature_name }}</p>
                <p class="text-xs text-slate-500">{{ feature.values.length }} wartości</p>
              </div>
              <font-awesome-icon icon="chevron-right" class="text-slate-400" />
            </div>
          </div>
        </div>
        <div v-else class="space-y-3">
          <!-- Back to features -->
          <div class="flex items-center space-x-2 mb-4">
            <button 
              type="button"
              class="text-blue-600 hover:text-blue-700 text-sm font-medium"
              @click="selectedFeature = null"
            >
              <font-awesome-icon icon="chevron-left" class="mr-1" />
              Powrót do cech
            </button>
          </div>
          
          <h4 class="text-sm font-semibold text-slate-700 mb-3">
            Wybierz wartość dla: {{ selectedFeature.feature_name }}
          </h4>
          
          <div class="space-y-2">
            <div 
              v-for="value in selectedFeature.values" 
              :key="value.id"
              class="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
            >
              <p class="text-sm text-slate-800">{{ value.label.split(': ')[1] || value.label }}</p>
              <button 
                type="button"
                class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors" 
                @click="selectFeatureValueFromBrowse(value)"
              >
                Wybierz
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted } from 'vue';
import BaseModal from '../../../shared/components/BaseModal.vue';
import { featureApi, type FeatureValueSearchResult, type FeatureWithValues, type SelectedFeatureValue } from '../services/features.api';
import { useApiConfig } from '../../../composables/useApi';

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{ 
  (e: 'close'): void; 
  (e: 'select', item: SelectedFeatureValue): void 
}>();

const search = ref('');
const inputEl = ref<HTMLInputElement | null>(null);
const loading = ref(false);
const loadingFeatures = ref(false);
const searchResults = ref<FeatureValueSearchResult[]>([]);
const allFeatures = ref<FeatureWithValues[]>([]);
const selectedFeature = ref<FeatureWithValues | null>(null);
const step = ref<'search' | 'browse'>('search');
const api = useApiConfig();

function debounce<F extends (...args: any[]) => any>(fn: F, delay = 300) {
  let t: any;
  return (...args: Parameters<F>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
}

const debouncedSearch = debounce(async () => {
  if (step.value !== 'search') return;
  
  const q = search.value.trim();
  const currentQuery = q; // Capture the query at the time of the API call
  
  loading.value = true;
  try {
    // Always call the API, it will handle empty queries by returning initial suggestions
    const results = await featureApi.searchFeatureValuesWithConfig(api, q);
    
    // Only update results if the search query hasn't changed since the API call started
    if (search.value.trim() === currentQuery) {
      searchResults.value = results;
    }
  } catch (e) {
    console.error('Feature values search failed', e);
    // Only clear results if the search query hasn't changed
    if (search.value.trim() === currentQuery) {
      searchResults.value = [];
    }
  } finally {
    loading.value = false;
  }
}, 300);

async function loadAllFeatures() {
  if (allFeatures.value.length > 0) return;
  
  loadingFeatures.value = true;
  try {
    allFeatures.value = await featureApi.getAllFeatureValuesWithConfig(api);
  } catch (e) {
    console.error('Failed to load all features', e);
    allFeatures.value = [];
  } finally {
    loadingFeatures.value = false;
  }
}

function selectFeature(feature: FeatureWithValues) {
  selectedFeature.value = feature;
}

function selectFeatureValue(result: FeatureValueSearchResult) {
  const selectedValue: SelectedFeatureValue = {
    id: result.id,
    label: result.label,
    feature_id: result.feature_id,
    feature_name: result.feature_name,
  };
  emit('select', selectedValue);
}

function selectFeatureValueFromBrowse(value: { id: number; label: string }) {
  if (!selectedFeature.value) return;
  
  const selectedValue: SelectedFeatureValue = {
    id: value.id,
    label: value.label,
    feature_id: selectedFeature.value.feature_id,
    feature_name: selectedFeature.value.feature_name,
  };
  
  emit('select', selectedValue);
}

watch(search, () => {
  if (step.value === 'search') {
    debouncedSearch();
  }
});

watch(step, (newStep) => {
  if (newStep === 'browse') {
    loadAllFeatures();
  } else if (newStep === 'search') {
    // Load initial suggestions when switching to search
    debouncedSearch();
    selectedFeature.value = null;
  } else {
    selectedFeature.value = null;
    if (search.value.trim().length >= 2) {
      debouncedSearch();
    }
  }
});

watch(() => props.show, async (v) => {
  if (v) {
    await nextTick();
    inputEl.value?.focus();
    step.value = 'search';
    selectedFeature.value = null;
    search.value = '';
    searchResults.value = [];
    // Load initial suggestions when modal opens
    debouncedSearch();
    
    // Preload features for faster browsing
    loadAllFeatures();
  }
});

onMounted(() => {
  // Preload features on component mount
  loadAllFeatures();
});
</script>
